import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { PaymentDetailsEntry } from '@/modules/settings/types/payment-details';
import { SettingsRoutes } from '@/router/routes';

import { UpdateInvoiceFormValues } from '../../validations';
import { InvoicePaymentDetailsSelect } from './invoice-payment-details-select';

interface InvoicePaymentDetailsCardProps {
  form: UseFormReturn<UpdateInvoiceFormValues>;
  isReadOnly: boolean;
  paymentMemo: string;
  noValue: string;
  entries: PaymentDetailsEntry[];
  isLoading: boolean;
}

/** Presents the selected payment memo and, when editing, its organization-level selector. */
export function InvoicePaymentDetailsCard({
  form,
  isReadOnly,
  paymentMemo,
  noValue,
  entries,
  isLoading,
}: InvoicePaymentDetailsCardProps) {
  const { t } = useTranslation('authenticated.invoices');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>{t('details.paymentDetails')}</CardTitle>
          {!isReadOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open(SettingsRoutes.Finance, '_blank', 'noopener,noreferrer')}
            >
              {t('details.managePaymentDetails')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className={isReadOnly ? undefined : 'space-y-4'}>
        {!isReadOnly && (
          <InvoicePaymentDetailsSelect form={form} entries={entries} isLoading={isLoading} />
        )}
        <Textarea
          value={paymentMemo}
          placeholder={noValue}
          aria-label={t('details.paymentDetails')}
          disabled
          className="min-h-28 resize-none"
        />
      </CardContent>
    </Card>
  );
}
